import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronLeft, Home, Calculator, RotateCcw, X } from 'lucide-react';
import Layout from '../../../../components/Layout';
import { Button } from '../../../../components/ui/Button';

const initialValues = { v: '', i: '', r: '', p: '' };

function InputRow({ letter, label, placeholder, value, onChange, onClear }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 overflow-hidden">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-l-md bg-gray-200 dark:bg-gray-600 text-white font-semibold text-sm">
        {letter}
      </div>
      <input
        type="number"
        inputMode="decimal"
        min="0"
        step="0.01"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 min-w-0 px-3 py-2.5 text-sm text-gray-900 dark:text-gray-100 bg-transparent border-0 focus:outline-none placeholder:text-gray-500 dark:placeholder:text-gray-400"
        aria-label={label}
      />
      <button
        type="button"
        onClick={onClear}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-500 hover:bg-red-600 text-white transition-colors mr-2"
        aria-label={`Clear ${label}`}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function OhmsLawCalc() {
  const location = useLocation();
  const isMobile = location.pathname.startsWith('/tenant/mobile');

  const [values, setValues] = useState(initialValues);

  const backPath = isMobile ? '/tenant/mobile/calculators' : '/tenant/tools/calculators';

  const updateValue = (field, val) => {
    setValues((p) => ({ ...p, [field]: val }));
  };

  const clearValue = (field) => {
    setValues((p) => ({ ...p, [field]: '' }));
  };

  const countFilled = () => {
    return ['v', 'i', 'r', 'p'].filter((f) => values[f] !== '' && values[f] != null).length;
  };

  const handleCalculate = () => {
    const filled = countFilled();
    if (filled !== 2) return;

    const v = parseFloat(values.v);
    const i = parseFloat(values.i);
    const r = parseFloat(values.r);
    const p = parseFloat(values.p);

    let newV = values.v !== '' ? v : null;
    let newI = values.i !== '' ? i : null;
    let newR = values.r !== '' ? r : null;
    let newP = values.p !== '' ? p : null;

    // Two values given - derive the other two
    if (newV != null && newI != null) {
      newR = newV / newI;
      newP = newV * newI;
    } else if (newV != null && newR != null) {
      newI = newV / newR;
      newP = (newV * newV) / newR;
    } else if (newV != null && newP != null) {
      newI = newP / newV;
      newR = (newV * newV) / newP;
    } else if (newI != null && newR != null) {
      newV = newI * newR;
      newP = newI * newI * newR;
    } else if (newI != null && newP != null) {
      newV = newP / newI;
      newR = newP / (newI * newI);
    } else if (newR != null && newP != null) {
      newI = Math.sqrt(newP / newR);
      newV = Math.sqrt(newP * newR);
    }

    setValues({
      v: newV != null ? String(newV) : '',
      i: newI != null ? String(newI) : '',
      r: newR != null ? String(newR) : '',
      p: newP != null ? String(newP) : '',
    });
  };

  const handleReset = () => {
    setValues({ ...initialValues });
  };

  const canCalculate = countFilled() === 2;

  const content = (
    <div className="max-w-2xl mx-auto">
      {/* Header bar */}
      <div className="flex items-center justify-between bg-green-600 dark:bg-green-700 px-4 py-3 rounded-t-lg -mx-4 -mt-4 mb-0 sm:rounded-t-none sm:mx-0 sm:mt-0 sm:-mx-0 sm:-mt-0 sm:rounded-lg">
        <Link
          to={backPath}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white hover:bg-white/20 transition-colors"
          aria-label="Back"
        >
          <ChevronLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-lg font-semibold text-white">Ohm&apos;s Law Calculator</h1>
        <Link
          to={isMobile ? '/tenant/mobile' : '/tenant/dashboard'}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white hover:bg-white/20 transition-colors"
          aria-label="Home"
        >
          <Home className="h-5 w-5" />
        </Link>
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-400 mt-4 mb-4">
        Enter two values to calculate the other two
      </p>

      <div className={`space-y-4 ${isMobile ? 'pb-8' : ''}`}>
        <InputRow
          letter="V"
          label="Voltage (Volts)"
          placeholder="Voltage (Volts)"
          value={values.v}
          onChange={(val) => updateValue('v', val)}
          onClear={() => clearValue('v')}
        />
        <InputRow
          letter="I"
          label="Current (Amps)"
          placeholder="Current (Amps)"
          value={values.i}
          onChange={(val) => updateValue('i', val)}
          onClear={() => clearValue('i')}
        />
        <InputRow
          letter="R"
          label="Resistance (Ohm's)"
          placeholder="Resistance (Ohm's)"
          value={values.r}
          onChange={(val) => updateValue('r', val)}
          onClear={() => clearValue('r')}
        />
        <InputRow
          letter="P"
          label="Power (Watts)"
          placeholder="Power (Watts)"
          value={values.p}
          onChange={(val) => updateValue('p', val)}
          onClear={() => clearValue('p')}
        />

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={handleCalculate}
            disabled={!canCalculate}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Calculator className="h-4 w-4" />
            Calculate
          </Button>
          <Button
            onClick={handleReset}
            variant="destructive"
            className="flex-1 py-3 flex items-center justify-center gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </Button>
        </div>

        {/* Definitions */}
        <div className="mt-6 space-y-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            <span className="font-bold">V</span>: Voltage measured in Volts
          </p>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            <span className="font-bold">I</span>: Current measured in Amps
          </p>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            <span className="font-bold">R</span>: Resistance measured in Ohm&apos;s
          </p>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            <span className="font-bold">P</span>: Power measured in Watts
          </p>
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div className="px-4 py-6 pb-8 min-h-screen bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
        {content}
      </div>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 px-4 sm:px-6 lg:px-8 py-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Ohm&apos;s Law Calculator
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Calculate voltage, current, resistance, or power from any two known values
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-6">
          {content}
        </div>
      </div>
    </Layout>
  );
}
